"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Loader2, Star } from "lucide-react";

import { cn } from "@/app/_lib/utils";
import { toast } from "@/app/_hooks/use-toast";
import { submitReview } from "../lib/action";
import {
  TurnstileWidget,
  type TurnstileWidgetHandle,
} from "@/app/_components/turnstile-widget";
import { FormError } from "@/app/_components/form-error";

export default function ReviewForm() {
  const router = useRouter();
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [rating, setRating] = React.useState(0);
  const [hover, setHover] = React.useState(0);
  const [publish, setPublish] = React.useState(true);
  const [anonymous, setAnonymous] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // Bot mitigation.
  const [turnstileToken, setTurnstileToken] = React.useState("");
  const [honeypot, setHoneypot] = React.useState("");
  const turnstileRef = React.useRef<TurnstileWidgetHandle>(null);
  const formRenderedAtRef = React.useRef(Date.now());

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 2 || message.trim().length < 10 || rating < 1) {
      setError("Add your name, a rating, and a few words (10+ characters).");
      return;
    }
    try {
      setLoading(true);
      const res = await submitReview({
        name,
        message,
        rating,
        role: role || undefined,
        anonymous,
        turnstileToken,
        honeypot,
        formRenderedAt: formRenderedAtRef.current,
      });
      if (res.success) {
        toast({
          title: "Thank you 🎉",
          description: "Your review was submitted for approval.",
        });
        setName("");
        setRole("");
        setMessage("");
        setRating(0);
        router.refresh();
      } else {
        setError(
          (res as { message?: string }).message ||
            "Something went wrong. Please try again.",
        );
        turnstileRef.current?.reset();
      }
    } catch {
      setError("Something went wrong. Please try again.");
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const input =
    "h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm outline-none focus:border-primary/50";

  return (
    <form
      onSubmit={onSubmit}
      className=" bg-[#DDECCF33] border border-primary shadow-[0px_0px_25px_0px_#0000001A] p-6 md:p-8"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">
            Your name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            className={input}
            style={{ fontSize: "16px" }}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-xs text-muted-foreground">
            Role &amp; company (optional)
          </label>
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Product lead, Acme"
            className={input}
            style={{ fontSize: "16px" }}
          />
        </div>
      </div>

      <div className="mt-5">
        <p className="mb-2 text-xs text-muted-foreground">Rating</p>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              className="focus:outline-none"
              aria-label={`${star} star`}
            >
              <Star
                size={26}
                className={cn(
                  "transition-colors",
                  star <= (hover || rating)
                    ? "fill-primary text-primary"
                    : "text-muted-foreground/40",
                )}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <label className="mb-1.5 block text-xs text-muted-foreground">
          Tell us about the project
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={8}
          placeholder="What did we do well? What could we have done better?"
          className="w-full rounded-xl border border-border bg-background p-3.5 text-sm outline-none focus:border-primary/50 resize-none"
          style={{ fontSize: "16px" }}
        />
      </div>

      <div className="mt-5 flex flex-col gap-2.5 text-sm text-muted-foreground">
        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={publish}
            onChange={(e) => setPublish(e.target.checked)}
            className="mt-0.5 accent-[color:hsl(var(--primary))]"
          />
          You may publish this review, with my name, on your website.
        </label>
        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
            className="mt-0.5 accent-[color:hsl(var(--primary))]"
          />
          Attribute me anonymously (e.g. &ldquo;Product lead, fintech
          scale-up&rdquo;).
        </label>
      </div>

      {/* Honeypot — hidden via CSS (not type="hidden") so bots that fill every
          field in the DOM still trip it. Real users never see or fill it. */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="review-website">Website</label>
        <input
          id="review-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} />

      {error && (
        <div className="mb-4">
          <FormError message={error} />
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
      >
        {loading ? (
          <Loader2 size={16} className="animate-spin" />
        ) : (
          "Send message"
        )}
      </button>
    </form>
  );
}
